import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { clearToken, getToken } from '@/api/client'
import { reservationApi } from '@/api/reservation'

/**
 * 지금 보고 있는 예약이 누구 것인지.
 *
 * <p>직원 앱과 달리 <b>사람이 아니라 예약 하나</b>가 단위입니다. 손님은 계정이
 * 없고, 확인한 그 예약만 봅니다. 예약이 둘이면 각각 확인해 들어옵니다 —
 * 레거시가 문자 링크로 한 건씩 보내던 방식과 같습니다.</p>
 *
 * <p>이름과 예약번호를 저장칸에 같이 둡니다. 열쇠만 남기면 화면을 새로
 * 열 때 "누구 예약인지" 를 보여 주려고 서버를 한 번 더 불러야 합니다.</p>
 */
const KEY = 'rcms.customer.session'

interface Saved {
  rsvNo: string
  guestName: string | null
}

function load(): Saved | null {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as Saved) : null
  } catch {
    // 사생활 보호 모드에서는 저장칸을 못 엽니다. 열쇠가 있으면 그것만으로 봅니다.
    return null
  }
}

export const useSessionStore = defineStore('session', () => {
  const saved = ref<Saved | null>(load())

  /**
   * 열쇠와 저장칸이 <b>둘 다</b> 있어야 들어온 것으로 봅니다.
   *
   * <p><b>computed 로 두면 안 됩니다.</b> <c>getToken()</c> 은 <c>localStorage</c> 를
   * 읽으므로 <b>반응하지 않습니다</b>. 처음 볼 때 열쇠가 없으면 <c>&&</c> 가 거기서
   * 끊겨 <c>saved</c> 를 <b>아예 읽지 않고</b>, 그러면 그 computed 는 매달릴 곳이
   * 하나도 없어 <b>false 로 굳어 버립니다</b> — 나중에 확인을 마쳐도 계속 false 입니다.</p>
   *
   * <p>2026-09-03 에 실제로 그랬습니다. 본인 확인은 되고 열쇠도 저장됐는데
   * 화면이 안 넘어갔습니다 — 길잡이가 이 값을 보고 되돌려보내고 있었습니다.</p>
   *
   * <p>그래서 <b>함수</b>로 둡니다. 부를 때마다 새로 봅니다.</p>
   */
  function isVerified(): boolean {
    return getToken() !== null && saved.value !== null
  }

  const rsvNo = computed(() => saved.value?.rsvNo ?? '')
  const guestName = computed(() => saved.value?.guestName ?? '')

  async function verify(rsvNoInput: string, telLast4: string): Promise<void> {
    const r = await reservationApi.verify(rsvNoInput.trim(), telLast4.trim())
    saved.value = { rsvNo: r.rsvNo, guestName: r.guestName }
    try {
      localStorage.setItem(KEY, JSON.stringify(saved.value))
    } catch {
      // 저장을 못 해도 이번 방문에서는 씁니다.
    }
  }

  function leave(): void {
    clearToken()
    saved.value = null
    try {
      localStorage.removeItem(KEY)
    } catch {
      // 지우지 못해도 열쇠가 없으면 못 봅니다.
    }
  }

  return { isVerified, rsvNo, guestName, verify, leave }
})
